import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import log from "loglevel";
import {createJsonPref, extractSimplePref, usePreferenceContext} from "../../../providers/PreferenceProvider";
import {TenantPreferences} from "../../../constants/constants";
import styled from "@emotion/styled";
import VehicleConfigurationTable from "./VehicleConfigurationTable";
import {DefaultVehicleIcons} from "../../vehicles/VehicleIconResolver";
import {useSetTenantPrefMutation} from "../../../generated/graphql";
import {isEqual} from "lodash";
import {useFeatureFlagContext} from "../../../providers/FeatureFlagProvider";

export type TVehiclePieceCountValue = {
  min: number | null;
  max: number | null;
};

export type TVehicleConfiguration = {
  id: string;
  value: string[];
  dwellTime: number | undefined;
  /** Sits beside dwellTime in dispatch.ui.manifest.vehicleIconSetup; absent until a bound is set. */
  pieceCount?: TVehiclePieceCountValue;
};

export const DefaultVehicleConfiguration: TVehicleConfiguration[] = DefaultVehicleIcons.map((icon) => ({
  id: icon.id,
  value: [],
  dwellTime: undefined,
  pieceCount: undefined
}));

/** A stored bound is only trusted when it is a non-negative integer; anything else reads as unset. */
const toStoredBound = (value: unknown): number | null =>
  typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : null;

/**
 * Collapses an empty range to undefined. JSON.stringify omits an undefined member, so a tenant that
 * never sets a piece count keeps the exact payload dwellTime alone has always produced.
 */
const toStoredPieceCount = (value: TVehiclePieceCountValue | undefined): TVehiclePieceCountValue | undefined => {
  const min = toStoredBound(value?.min);
  const max = toStoredBound(value?.max);
  return min === null && max === null ? undefined : {min, max};
};

const VehicleConfiguration = () => {
  const [configuration, setConfiguration] = useState(DefaultVehicleConfiguration);
  const {tenantPreferences, tenantPrefsQueryRefetch} = usePreferenceContext();
  const [setTenantPref] = useSetTenantPrefMutation();
  const {constraintsIP: constraintsIPEnabled} = useFeatureFlagContext();

  // Authoritative copy of `configuration`, updated synchronously alongside setConfiguration. Writers
  // read this instead of closing over the state value, so a debounced write armed on an earlier
  // render can never re-serialize a stale array and drop another row's just-saved value.
  const configurationRef = useRef(configuration);
  const applyConfiguration = useCallback((next: TVehicleConfiguration[]) => {
    configurationRef.current = next;
    setConfiguration(next);
  }, []);

  // The piece count debounce flushes on unmount, so a write can still be in flight once the settings
  // panel is gone. Reverting then would touch an unmounted component.
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  const existingAssociatedStrings = useMemo(
    () =>
      configuration?.length
        ? configuration
            .map((item) => item.value)
            .flat()
            .map((value) => value.toLocaleLowerCase())
        : [],
    [configuration]
  );

  useEffect(() => {
    const vehicleConfigurationPref = extractSimplePref(tenantPreferences, TenantPreferences.vehicleIconSetup, [])
      .value as TVehicleConfiguration[];

    const mappedConfiguration = DefaultVehicleConfiguration.map((item) => {
      const configuredItem = vehicleConfigurationPref.find((pref) => pref.id === item.id);
      return configuredItem
        ? {
            ...item,
            value: configuredItem?.value,
            dwellTime: configuredItem?.dwellTime,
            pieceCount: toStoredPieceCount(configuredItem?.pieceCount)
          }
        : item;
    });

    if (!isEqual(configuration, mappedConfiguration)) {
      applyConfiguration(mappedConfiguration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * One preference, one mutation: piece count rides the same payload as dwellTime, so a rejected
   * write restores one coherent snapshot — no second key is left holding a divergent value.
   */
  const writeConfiguration = useCallback(
    (updatedConfiguration: TVehicleConfiguration[], previousConfiguration: TVehicleConfiguration[]) => {
      const updatedPref = updatedConfiguration.filter((item) => item.value.length > 0 || item.dwellTime !== undefined);

      //Update component state to avoid delay
      applyConfiguration(updatedConfiguration);
      setTenantPref({
        variables: {name: TenantPreferences.vehicleIconSetup, input: createJsonPref(updatedPref)}
      })
        .then(() => {
          tenantPrefsQueryRefetch?.();
        })
        .catch((error) => {
          log.error("Failed to update vehicle icon setup preference:", error);
          // Optionally, revert to previous values if the update fails to maintain UI consistency
          if (isMountedRef.current) {
            applyConfiguration(previousConfiguration);
          }
        });
    },
    [applyConfiguration, setTenantPref, tenantPrefsQueryRefetch]
  );

  /**
   * `clearPieceCount` is set by the row when its last associated string is removed. It is deliberately
   * not gated on constraintsIP: with the column hidden a tenant would otherwise keep stranded bounds
   * in local state and re-persist them on the next edit.
   *
   * `prefItem.pieceCount` is ignored on purpose — the row's copy can be a render behind, while
   * configurationRef always holds the newest bounds.
   */
  const handleUpdateConfiguration = useCallback(
    (prefItem: TVehicleConfiguration, clearPieceCount = false) => {
      const previousConfiguration = configurationRef.current;
      const updatedConfiguration = previousConfiguration.map((item) =>
        item.id === prefItem.id
          ? {
              ...item,
              value: prefItem.value,
              dwellTime: prefItem.dwellTime,
              pieceCount: clearPieceCount ? undefined : item.pieceCount
            }
          : item
      );

      writeConfiguration(updatedConfiguration, previousConfiguration);
    },
    [writeConfiguration]
  );

  const handleUpdatePieceCount = useCallback(
    (id: string, pieceCount: TVehiclePieceCountValue) => {
      const previousConfiguration = configurationRef.current;
      const updatedConfiguration = previousConfiguration.map((item) =>
        item.id === id ? {...item, pieceCount: toStoredPieceCount(pieceCount)} : item
      );

      writeConfiguration(updatedConfiguration, previousConfiguration);
    },
    [writeConfiguration]
  );

  return (
    <Container>
      <VehicleConfigurationTable
        configuration={configuration.slice(0, 13)}
        showPieceCount={constraintsIPEnabled}
        existingAssociatedStrings={existingAssociatedStrings}
        onUpdateConfiguration={handleUpdateConfiguration}
        onUpdatePieceCount={handleUpdatePieceCount}
      />
      <VehicleConfigurationTable
        configuration={configuration.slice(13)}
        showPieceCount={constraintsIPEnabled}
        existingAssociatedStrings={existingAssociatedStrings}
        onUpdateConfiguration={handleUpdateConfiguration}
        onUpdatePieceCount={handleUpdatePieceCount}
      />
    </Container>
  );
};

export default VehicleConfiguration;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  padding: 0 24px 24px;
`;
